var base = require('./base'),
    millicolor = require('lib/color-object'),
    toVec3f = base.toVec3f,
    toRotation = base.toRotation;

/**
 * @class An instance of the SectionPlane object is used to control the interactive section plane object in the 3D scene.
 * @see {@link module:solo.app.addSectionPlane app.addSectionPlane}
 * @see {@link module:solo.app.removeSectionPlane app.removeSectionPlane}
 */
function SectionPlane(peer) {
    Object.defineProperty(this, Symbol.for('peer'), { value: peer, writable: true });
}

var sectionPlaneRepresentations = {};
Object.defineProperties(sectionPlaneRepresentations, {
    'NONE': { value: 0, enumerable: true },
    'PLANE': { value: 1, enumerable: true },
    'OUTLINE': { value: 2, enumerable: true },
});
/**
 * @memberof SectionPlane
 * @static
 * @readonly
 * @member {object} VisualRepresentations
 * @prop {number} NONE 0
 * @prop {number} PLANE 1
 * @prop {number} OUTLINE 2
 */
Object.defineProperty(SectionPlane, 'VisualRepresentations', { value: sectionPlaneRepresentations });

Object.defineProperties(SectionPlane.prototype, {
    /**
     * Enables or disables the section plane.
     * @memberof SectionPlane
     * @member {boolean} enabled
     */
    'enabled': {
        get: function () { return !!Module._sectionPlane_isEnabled(this[Symbol.for('peer')]); },
        set: function (enable) { Module._sectionPlane_enable(this[Symbol.for('peer')], enable); },
        enumerable: true
    },
    /**
     * Enables or disables user interaction with the section plane.
     * @memberof SectionPlane
     * @member {boolean} userInteractionEnabled
     */
    'userInteractionEnabled': {
        get: function () { return !!Module._sectionPlane_isUserInteractionEnabled(this[Symbol.for('peer')]); },
        set: function (enable) { Module._sectionPlane_enableUserInteraction(this[Symbol.for('peer')], enable); },
        enumerable: true
    },
    /**
     * Gets or sets a visual representation of the section plane.
     * @memberof SectionPlane
     * @member {number} visualRepresentation
     */
    'visualRepresentation': {
        get: function () { return Module._sectionPlane_getVisualRepresentation(this[Symbol.for('peer')]); },
        set: function (representatoin) { Module._sectionPlane_setVisualRepresentation(this[Symbol.for('peer')], representatoin); },
        enumerable: true
    },
    /**
     * Gets or sets a position of the section plane. An array of three floats [x, y, z].
     * @memberof SectionPlane
     * @member {number[]} position 
     */
    'position': {
        get: function () {
            var p = Module._sectionPlane_getPosition(this[Symbol.for('peer')]) >>> 2;
            return Array.from(HEAPF32.subarray(p, p + 3));
        },
        set: function (position) {
            var sp = stackSave();
            Module._sectionPlane_setPosition(this[Symbol.for('peer')], toVec3f(position));
            stackRestore(sp);
        },
        enumerable: true
    },
    /**
     * Gets or sets an orientation of the section plane. An array of four floats [x, y, z, angle].
     * @memberof SectionPlane
     * @member {number[]} orientation
     */
    'orientation': {
        get: function () {
            var p = Module._sectionPlane_getOrientation(this[Symbol.for('peer')]) >>> 2;
            return Array.from(HEAPF32.subarray(p, p + 4));
        },
        set: function (orientation) {
            var sp = stackSave();
            Module._sectionPlane_setOrientation(this[Symbol.for('peer')], toRotation(orientation));
            stackRestore(sp);
        },
        enumerable: true
    },
    /**
     * Gets or sets a color of the section plane cap. An array of tree floats [r, g, b], where r,g,b := [0...1]
     * @memberof SectionPlane
     * @member {number[]} color
     */
    'color': {
        get: function () {
            var p = Module._sectionPlane_getColor(this[Symbol.for('peer')]) >>> 2;
            return Array.from(HEAPF32.subarray(p, p + 3));
        },
        set: function (color) {
            var sp = stackSave();
            Module._sectionPlane_setColor(this[Symbol.for('peer')], toVec3f(millicolor(color).toArray()));
            stackRestore(sp);
        },
        enumerable: true
    },
    /**
     * Moves the center of the section plane to the vector specified by the translation parameter.
     * @memberof SectionPlane
     * @method moveBy
     * @param {number[]} translation An array of three floats [x, y, z].
     * @param {boolean} [animated=false]
     */
    'moveBy': {
        value: function (translation, animated) {
            var sp = stackSave();
            Module._sectionPlane_moveBy(this[Symbol.for('peer')], toVec3f(translation), animated);
            stackRestore(sp);
        },
        enumerable: true
    },
    /**
     * Rotates the section plane in the direction specified by the rotation parameter.
     * @memberof SectionPlane
     * @method rotate
     * @param {number[]} rotation An array of four floats [x, y, z, angle].
     * @param {boolean} [animated=false]
     */
    'rotate': {
        value: function (rotation, animated) {
            var sp = stackSave();
            Module._sectionPlane_rotate(this[Symbol.for('peer')], toRotation(rotation), animated);
            stackRestore(sp);
        },
        writable: false,
        enumerable: true
    },
    /**
     * Flips the section plane.
     * @memberof SectionPlane
     * @method flip
     * @param {boolean} [animated=false]
     */
    'flip': {
        value: function (animated) { this.rotate([0, 1, 0, Math.PI], animated); },
        enumerable: true
    },
});

module.exports = SectionPlane;